import { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../config/firebase';

/**
 * Organization Notes - Shared knowledge base
 *
 * Features:
 * - Notes list with meeting name, author, date
 * - Search and filter
 * - Note preview
 */

export default function OrgNotes({ organization, members }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNote, setSelectedNote] = useState(null);

  useEffect(() => {
    if (organization?.id) {
      loadNotes();
    }
  }, [organization?.id]);

  const loadNotes = async () => {
    try {
      setLoading(true);
      const notesRef = collection(db, `organizations/${organization.id}/notes`);
      const q = query(notesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const notesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));

      setNotes(notesData);
    } catch (error) {
      console.error('Error loading notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredNotes = notes.filter(note =>
    note.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    note.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    note.meetingTitle?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getMemberEmail = (userId) => {
    const member = members.find(m => m.userId === userId || m.id === userId);
    return member?.email?.split('@')[0] || 'Unknown';
  };

  const truncateContent = (content, maxLength = 100) => {
    if (!content) return '';
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Notes</h2>
          <p className="text-white/50 text-sm mt-1">{notes.length} shared notes</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          placeholder="Search notes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#06c]/50"
        />
      </div>

      {/* Notes Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-white/20 border-t-[#06c] rounded-full animate-spin" />
        </div>
      ) : filteredNotes.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredNotes.map(note => (
            <div
              key={note.id}
              onClick={() => setSelectedNote(note)}
              className="group backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 p-5 cursor-pointer hover:border-white/25 hover:from-white/15 transition-all"
            >
              {/* Title */}
              <h3 className="text-white font-medium truncate">{note.title || 'Untitled Note'}</h3>

              {/* Meeting reference */}
              {note.meetingTitle && (
                <div className="flex items-center gap-2 mt-2">
                  <svg className="w-4 h-4 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span className="text-white/50 text-xs truncate">{note.meetingTitle}</span>
                </div>
              )}

              {/* Preview */}
              <p className="text-white/50 text-sm mt-3 line-clamp-3">
                {truncateContent(note.content, 120)}
              </p>

              {/* Meta */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                <span className="text-white/40 text-xs">{getMemberEmail(note.userId)}</span>
                <span className="text-white/40 text-xs">{formatDate(note.createdAt)}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15">
          <div className="text-5xl mb-4">📝</div>
          <div className="text-white/50">No notes yet</div>
          <div className="text-white/30 text-sm mt-1">Notes from meetings will appear here</div>
        </div>
      )}

      {/* Note Detail Modal */}
      {selectedNote && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedNote(null)}>
          <div className="bg-[#1c1c1e] rounded-2xl border border-white/10 max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <h3 className="text-white font-semibold">{selectedNote.title || 'Untitled Note'}</h3>
                <p className="text-white/50 text-xs mt-1">
                  {getMemberEmail(selectedNote.userId)} • {formatDate(selectedNote.createdAt)}
                </p>
              </div>
              <button onClick={() => setSelectedNote(null)} className="p-2 rounded-lg hover:bg-white/10">
                <svg className="w-5 h-5 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {selectedNote.meetingTitle && (
                <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-white/5">
                  <svg className="w-4 h-4 text-[#06c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span className="text-white/70 text-sm">From: {selectedNote.meetingTitle}</span>
                </div>
              )}

              <div className="text-white/80 text-sm whitespace-pre-wrap leading-relaxed">
                {selectedNote.content || 'No content'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
